import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle as CircleCheck, ExternalLink, Menu, X } from 'lucide-react';
import { useTestStore } from '../../store/testStore';

const Header: React.FC = () => {
  const location = useLocation();
  const { isTestStarted, isTestCompleted } = useTestStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  
  return (
    <motion.header 
      className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="container-custom py-4">
        <div className="flex justify-between items-center">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <CircleCheck className="h-8 w-8 text-primary-600" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-xl font-bold text-gray-900">
                <span className="hidden sm:inline">Export Readiness Test</span>
                <span className="sm:hidden">ERT</span>
              </h1>
            </motion.div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:block">
            <ul className="flex space-x-6 items-center">
              <li>
                <Link 
                  to="/" 
                  className={`text-sm font-medium hover:text-primary-600 transition-colors ${
                    location.pathname === '/' ? 'text-primary-600' : 'text-gray-700'
                  }`}
                >
                  Home
                </Link>
              </li>
              
              {isTestStarted && !isTestCompleted && (
                <li>
                  <Link 
                    to="/test" 
                    className={`text-sm font-medium hover:text-primary-600 transition-colors ${
                      location.pathname === '/test' ? 'text-primary-600' : 'text-gray-700'
                    }`}
                  >
                    Continue Test
                  </Link>
                </li>
              )}
              
              {isTestCompleted && (
                <li>
                  <Link 
                    to="/results" 
                    className={`text-sm font-medium hover:text-primary-600 transition-colors ${
                      location.pathname === '/results' ? 'text-primary-600' : 'text-gray-700'
                    }`}
                  >
                    View Results
                  </Link>
                </li>
              )}
              
              <li>
                <a 
                  href="https://www.trade.gov/export-solutions" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                >
                  Resources <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 border-t border-gray-100 pt-4"
            >
              <nav>
                <ul className="space-y-4">
                  <li>
                    <Link 
                      to="/" 
                      onClick={closeMobileMenu}
                      className={`block py-2 px-4 rounded-lg text-base font-medium transition-colors ${
                        location.pathname === '/' 
                          ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Home
                    </Link>
                  </li>
                  
                  {isTestStarted && !isTestCompleted && (
                    <li>
                      <Link 
                        to="/test" 
                        onClick={closeMobileMenu}
                        className={`block py-2 px-4 rounded-lg text-base font-medium transition-colors ${
                          location.pathname === '/test' 
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Continue Test
                      </Link>
                    </li>
                  )}
                  
                  {isTestCompleted && (
                    <li>
                      <Link 
                        to="/results" 
                        onClick={closeMobileMenu}
                        className={`block py-2 px-4 rounded-lg text-base font-medium transition-colors ${
                          location.pathname === '/results' 
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        View Results
                      </Link>
                    </li>
                  )}
                  
                  <li>
                    <a 
                      href="https://www.trade.gov/export-solutions" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      onClick={closeMobileMenu}
                      className="flex items-center py-2 px-4 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Export Resources 
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </li>
                </ul>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;